// =============================================================================
// app/src/paginas/Parceiros.tsx — os pedidos do portal B2B (/parceiros).
//
// Pedido do dono (24/08/2026): oficina/frota/concessionária pedem pelo portal
// (clientes.serginhoautopeças.com.br) e a loja responde AQUI: a fila por
// situação, o histórico por cliente ("quanto já pediram") e a gestão dos
// logins. Gerente e dono — a RLS deixa o vendedor LER os pedidos (quem separa
// peça é o balcão), mas a gestão é desta tela.
//
// TRÊS ABAS, DUAS RÉGUAS (28/09/2026). "Funcionário não pode ver faturamento,
// nem quanto vende", e gerente é funcionário:
//
//   Pedidos / Clientes B2B  → só o DONO. O pedido carrega `total`, e o resumo
//       por cliente é a soma do que ele já pediu: é faturamento do canal B2B
//       com outro nome. O banco concorda — a policy de `partner_orders` e o
//       exige_papel de resumo_parceiros_b2b passaram a exigir dono.
//
//   Acessos  → gerente e dono, como sempre. Criar o login do parceiro e mexer
//       no desconto dele não é faturamento, e a RLS §17 continua concedendo as
//       duas coisas ao gerente (select em partner_accounts e update só da
//       coluna desconto_pct). Foi por isso que a rota inteira NÃO virou de
//       dono: fechá-la tiraria do gerente um trabalho que o banco lhe dá.
//
// A esteira é a do banco (062): novo → confirmado → pronto → entregue;
// cancelado sai de novo/confirmado e é da gerência. "Confirmado" é o momento
// de encomendar do fornecedor o que estiver marcado SOB ENCOMENDA.
// =============================================================================
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SituacaoPedidoB2B } from '@core/types';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import CampoNumero from '../componentes/CampoNumero';
import Segmentado from '../componentes/Segmentado';
import {
  alternarAtivacaoParceiro, atualizarDescontoParceiro, avancarPedidoB2B,
  buscarClientes, contasDeParceiros, criarParceiro, excluirParceiro,
  listarPedidosB2B, redefinirSenhaParceiro, resumoParceirosB2B,
  type PedidoB2B,
} from '../lib/consultas';
import { brl, dataHoraBR } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import { podeAdministrar, podeVerFaturamento, useSessao } from '../lib/sessao';

// 'todos' não é situação do banco: é o filtro da tela. Existe porque "Ver
// pedidos deste cliente" caía numa situação fixa e mostrava lista vazia para
// quem tinha pedido em outra etapa (achado da auditoria, 25/08/2026).
type FiltroSituacao = SituacaoPedidoB2B | 'todos';

const SITUACOES: ReadonlyArray<{ id: FiltroSituacao; rotulo: string }> = [
  { id: 'todos', rotulo: 'Todos' },
  { id: 'novo', rotulo: 'Novos' },
  { id: 'confirmado', rotulo: 'Confirmados' },
  { id: 'pronto', rotulo: 'Prontos' },
  { id: 'entregue', rotulo: 'Entregues' },
  { id: 'cancelado', rotulo: 'Cancelados' },
];

const FORMA: Record<string, string> = {
  pix: 'pix', dinheiro: 'dinheiro', debito: 'débito', credito: 'crédito',
  boleto: 'boleto', faturado: 'faturado (a prazo)',
};

/** A próxima etapa da esteira — null quando não há para onde avançar. */
const PROXIMA: Partial<Record<SituacaoPedidoB2B, { para: SituacaoPedidoB2B; rotulo: string }>> = {
  novo: { para: 'confirmado', rotulo: 'Confirmar pedido' },
  confirmado: { para: 'pronto', rotulo: 'Marcar pronto' },
  pronto: { para: 'entregue', rotulo: 'Marcar entregue' },
};

export default function Parceiros() {
  const cliente = useQueryClient();
  const { papel } = useSessao();
  const dono = podeAdministrar(papel);
  // Quem vê dinheiro de pedido: só o dono. Quem não vê abre direto nos
  // Acessos — é a única aba que lhe pertence, e cair numa aba vazia é pior do
  // que não ter a aba.
  const verFaturamento = podeVerFaturamento(papel);
  const [aba, setAba] = useState<'pedidos' | 'clientes' | 'acessos'>(
    verFaturamento ? 'pedidos' : 'acessos',
  );
  const [situacao, setSituacao] = useState<FiltroSituacao>('novo');
  const [filtroCliente, setFiltroCliente] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  const [cancelando, setCancelando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');

  // As duas consultas com dinheiro nem saem do aparelho para quem não pode
  // vê-lo: a policy devolveria lista vazia (pedidos) e a RPC devolveria 42501
  // (resumo) — faixa vermelha numa tela que, para o gerente, está funcionando.
  const pedidos = useQuery({
    queryKey: ['parceiros', 'pedidos'], queryFn: listarPedidosB2B,
    enabled: verFaturamento,
  });
  const resumo = useQuery({
    queryKey: ['parceiros', 'resumo'], queryFn: resumoParceirosB2B,
    enabled: verFaturamento && aba === 'clientes',
  });
  const contas = useQuery({
    queryKey: ['parceiros', 'contas'], queryFn: contasDeParceiros, enabled: aba === 'acessos',
  });

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['parceiros'] });
    // O sino conta os novos — muda junto.
    cliente.invalidateQueries({ queryKey: ['sino', 'pedidos-b2b'] });
  };

  const avancar = useMutation({
    mutationFn: (p: { id: string; para: SituacaoPedidoB2B; motivo?: string }) =>
      avancarPedidoB2B(p.id, p.para, p.motivo),
    onSuccess: () => { setCancelando(null); setMotivo(''); recarregar(); },
  });

  const lista = useMemo(() => (pedidos.data ?? [])
    .filter((p) => situacao === 'todos' || p.situacao === situacao)
    .filter((p) => !filtroCliente || p.customer_id === filtroCliente), [pedidos.data, situacao, filtroCliente]);

  const contagem = useMemo(() => {
    const c = new Map<SituacaoPedidoB2B, number>();
    for (const p of pedidos.data ?? []) c.set(p.situacao, (c.get(p.situacao) ?? 0) + 1);
    return c;
  }, [pedidos.data]);

  const sobEncomenda = (p: PedidoB2B) =>
    p.partner_order_items.filter((i) => !i.pronta_entrega).length;

  // O título diz a verdade do que a pessoa tem na tela: para quem não vê o
  // total, esta é a tela dos ACESSOS do parceiro, não a dos pedidos.
  return (
    <Layout titulo={verFaturamento ? 'Parceiros — pedidos B2B' : 'Parceiros — acessos'}>
      <div className="space-y-4">
        {/* As duas primeiras abas só existem para quem vê o total (28/09/2026).
            Some a aba, não só o conteúdo: aba que abre vazia é defeito aos
            olhos de quem a toca. Com uma aba só, o Segmentado não aparece —
            não há entre o que escolher. */}
        {verFaturamento && (
          <Segmentado
            itens={[{ id: 'pedidos', rotulo: `Pedidos${contagem.get('novo') ? ` (${contagem.get('novo')} novos)` : ''}` },
                    { id: 'clientes', rotulo: 'Clientes B2B' },
                    { id: 'acessos', rotulo: 'Acessos' }] as const}
            valor={aba}
            aoMudar={(a) => { setAba(a); setAberto(null); setCancelando(null); }}
          />
        )}

        {pedidos.error ? <Aviso tipo="erro">{mensagemDeErro(pedidos.error)}</Aviso> : null}
        {avancar.error ? <Aviso tipo="erro">{mensagemDeErro(avancar.error)}</Aviso> : null}

        {verFaturamento && aba === 'pedidos' && (
          <>
            <Segmentado
              itens={SITUACOES.map((s) => {
                const n = s.id === 'todos'
                  ? (pedidos.data ?? []).length
                  : contagem.get(s.id) ?? 0;
                return { id: s.id, rotulo: `${s.rotulo}${n ? ` (${n})` : ''}` };
              })}
              valor={situacao}
              aoMudar={(s) => { setSituacao(s); setAberto(null); setCancelando(null); }}
            />
            {filtroCliente && (
              <p className="text-xs text-mute">
                Mostrando só os pedidos de um cliente.{' '}
                <button type="button" className="underline" onClick={() => setFiltroCliente(null)}>
                  Mostrar todos
                </button>
              </p>
            )}

            {pedidos.isPending && <Carregando texto="Buscando os pedidos" />}
            <ul className="space-y-2">
              {lista.map((p) => {
                const proxima = PROXIMA[p.situacao];
                const pendencias = sobEncomenda(p);
                return (
                  <li key={p.id} className="cartao p-3">
                    <button type="button" className="w-full text-left"
                            onClick={() => setAberto(aberto === p.id ? null : p.id)}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-bold">
                            <span className="font-mono">{p.codigo}</span> · {p.cliente_nome}
                          </p>
                          <p className="text-xs text-mute">
                            {dataHoraBR(p.created_at)} · {FORMA[p.forma_pagamento] ?? p.forma_pagamento}
                            {p.desconto_pct > 0 ? ` · desconto ${String(p.desconto_pct).replace('.', ',')}%` : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="tabular-nums text-lg font-black">{brl(Number(p.total))}</p>
                          {pendencias > 0 && (
                            <p className="text-xs font-semibold text-alerta">
                              {pendencias} item(ns) sob encomenda
                            </p>
                          )}
                        </div>
                      </div>
                    </button>

                    {aberto === p.id && (
                      <div className="mt-3 space-y-3 border-t border-edge pt-3">
                        <ul className="space-y-1">
                          {p.partner_order_items.map((i) => (
                            <li key={i.id} className="flex items-baseline justify-between gap-2 text-sm">
                              <span className="min-w-0">
                                <span className="font-mono text-xs text-dim">{i.sku}</span>{' '}
                                {i.descricao}
                                {!i.pronta_entrega && (
                                  <span className="ml-1 text-xs font-semibold text-alerta">
                                    sob encomenda
                                  </span>
                                )}
                              </span>
                              <span className="shrink-0 tabular-nums">
                                {String(Number(i.quantidade)).replace('.', ',')} × {brl(Number(i.preco_unit))}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {p.observacao && (
                          <p className="text-xs text-mute">Observação do parceiro: {p.observacao}</p>
                        )}
                        {p.cancelado_motivo && (
                          <p className="text-xs text-erro">Cancelado: {p.cancelado_motivo}</p>
                        )}

                        <div className="flex flex-wrap gap-2">
                          {proxima && (
                            <button type="button" className="botao-primario px-3 py-1.5 text-xs"
                                    disabled={avancar.isPending}
                                    onClick={() => avancar.mutate({ id: p.id, para: proxima.para })}>
                              {proxima.rotulo}
                            </button>
                          )}
                          {(p.situacao === 'novo' || p.situacao === 'confirmado') && (
                            <button type="button" className="botao-terciario"
                                    onClick={() => {
                                      setMotivo('');
                                      setCancelando(cancelando === p.id ? null : p.id);
                                    }}>
                              Cancelar pedido
                            </button>
                          )}
                        </div>

                        {cancelando === p.id && (
                          <div className="space-y-2 border border-erro/40 bg-erro/5 p-3">
                            <p className="text-xs text-mute">
                              Cancelar desfaz a expectativa do parceiro — ele vê a situação
                              no portal. Só gerência cancela; diga o motivo.
                            </p>
                            <input className="campo" placeholder="motivo (o parceiro NÃO vê)"
                                   value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                            <div className="flex gap-2">
                              <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                                      onClick={() => setCancelando(null)}>Voltar</button>
                              <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                                      disabled={avancar.isPending}
                                      onClick={() => avancar.mutate({ id: p.id, para: 'cancelado', motivo })}>
                                Cancelar de vez
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
              {lista.length === 0 && !pedidos.isPending && (
                <li className="text-sm text-dim">
                  {situacao === 'novo'
                    ? 'Nenhum pedido aguardando — o sino avisa quando chegar.'
                    : 'Nada aqui.'}
                </li>
              )}
            </ul>
          </>
        )}

        {verFaturamento && aba === 'clientes' && (
          <>
            {resumo.isPending && <Carregando />}
            {resumo.error ? <Aviso tipo="erro">{mensagemDeErro(resumo.error)}</Aviso> : null}
            <ul className="space-y-2">
              {(resumo.data ?? []).map((r) => (
                <li key={r.customer_id} className={`cartao flex flex-wrap items-center justify-between gap-3 p-3 ${
                  !r.ativo ? 'opacity-50' : ''}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-bold">
                      {r.nome} {!r.ativo && <span className="text-xs text-dim">(desativado)</span>}
                    </p>
                    <p className="text-xs text-mute">
                      desconto {String(r.desconto_pct).replace('.', ',')}%
                      {r.ultimo_em ? ` · último pedido ${dataHoraBR(r.ultimo_em)}` : ' · nunca pediu'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="tabular-nums text-lg font-black">{brl(Number(r.total))}</p>
                      <p className="text-xs text-dim">{r.pedidos} pedido(s)</p>
                    </div>
                    <button type="button" className="botao-terciario"
                            onClick={() => {
                              setFiltroCliente(r.customer_id);
                              setAba('pedidos');
                              setSituacao('todos');
                            }}>
                      Ver pedidos
                    </button>
                  </div>
                </li>
              ))}
              {(resumo.data ?? []).length === 0 && !resumo.isPending && (
                <li className="text-sm text-dim">
                  Nenhum parceiro ainda — crie o primeiro acesso na aba ao lado.
                </li>
              )}
            </ul>
          </>
        )}

        {aba === 'acessos' && <Acessos dono={dono} aoMudar={recarregar} contas={contas} />}
      </div>
    </Layout>
  );
}

// -----------------------------------------------------------------------------
// A gestão dos logins. Criar/senha/desativar passam pelo AGENTE da VPS
// (service key + banimento real no GoTrue); só o desconto é update direto —
// é a única coluna concedida (rls.sql §17).
function Acessos({ dono, aoMudar, contas }: {
  dono: boolean;
  aoMudar: () => void;
  contas: ReturnType<typeof useQuery<Awaited<ReturnType<typeof contasDeParceiros>>>>;
}) {
  const [criando, setCriando] = useState(false);
  const [busca, setBusca] = useState('');
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [desconto, setDesconto] = useState<number | null>(10);
  const [senhaDe, setSenhaDe] = useState<string | null>(null);
  const [senhaNova, setSenhaNova] = useState('');
  const [descontoDe, setDescontoDe] = useState<string | null>(null);
  const [descontoNovo, setDescontoNovo] = useState<number | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  // Desativar derruba a conta do parceiro na hora (bane no GoTrue): dois
  // passos, como todo destrutivo da casa. Reativar continua em um clique.
  const [desativando, setDesativando] = useState<string | null>(null);

  const clientes = useQuery({
    queryKey: ['parceiros', 'busca-cliente', busca],
    queryFn: () => buscarClientes(busca, 8),
    enabled: criando && busca.trim().length >= 2,
  });

  const limpar = () => {
    setCriando(false); setBusca(''); setClienteId(null);
    setEmail(''); setSenha(''); setDesconto(10);
  };

  const criar = useMutation({
    mutationFn: () => criarParceiro({
      customer_id: clienteId!, email: email.trim(), senha,
      desconto_pct: desconto ?? undefined,
    }),
    onSuccess: () => { limpar(); aoMudar(); },
  });
  const redefinir = useMutation({
    mutationFn: () => redefinirSenhaParceiro(senhaDe!, senhaNova),
    onSuccess: () => { setSenhaDe(null); setSenhaNova(''); },
  });
  const alternar = useMutation({
    mutationFn: (p: { id: string; desativar: boolean }) =>
      alternarAtivacaoParceiro(p.id, p.desativar),
    onSuccess: aoMudar,
  });
  const salvarDesconto = useMutation({
    mutationFn: () => atualizarDescontoParceiro(descontoDe!, descontoNovo!),
    onSuccess: () => { setDescontoDe(null); setDescontoNovo(null); aoMudar(); },
  });
  const excluir = useMutation({
    mutationFn: (id: string) => excluirParceiro(id),
    onSuccess: () => { setExcluindo(null); aoMudar(); },
  });

  // A consulta ENTRA na lista: sem ela, uma falha ao listar os acessos virava
  // "Nenhum acesso criado ainda" — a tela dizia que não há parceiro quando o
  // que houve foi um erro de rede (achado da auditoria, 25/08/2026).
  const erro = [contas.error, criar.error, redefinir.error, alternar.error,
                salvarDesconto.error, excluir.error].find(Boolean);

  return (
    <div className="space-y-3">
      {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}

      {!criando ? (
        <button type="button" className="botao-primario px-3 py-2 text-sm"
                onClick={() => setCriando(true)}>
          + Acesso de parceiro
        </button>
      ) : (
        <div className="cartao space-y-3 p-4">
          <p className="rotulo">Novo acesso</p>
          <p className="text-xs text-mute">
            O parceiro precisa estar no cadastro de clientes. Você define e-mail e
            senha e entrega a ele — ninguém se cadastra sozinho no portal.
          </p>
          <div>
            <label className="rotulo" htmlFor="pa-busca">Cliente</label>
            <input id="pa-busca" className="campo" placeholder="nome, CNPJ ou telefone…"
                   value={busca}
                   onChange={(e) => { setBusca(e.target.value); setClienteId(null); }} />
            {criando && busca.trim().length >= 2 && !clienteId && (
              <ul className="mt-1 space-y-1">
                {(clientes.data ?? []).map((c) => (
                  <li key={c.id}>
                    <button type="button"
                            className="w-full border border-edge px-3 py-1.5 text-left text-sm hover:border-signal"
                            onClick={() => { setClienteId(c.id); setBusca(c.nome); }}>
                      {c.nome} {c.cpf ? <span className="text-xs text-dim">· {c.cpf}</span> : null}
                    </button>
                  </li>
                ))}
                {(clientes.data ?? []).length === 0 && !clientes.isPending && (
                  <li className="text-xs text-dim">
                    Ninguém com esse nome — cadastre primeiro na tela Clientes.
                  </li>
                )}
              </ul>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="rotulo" htmlFor="pa-email">E-mail de acesso</label>
              <input id="pa-email" type="email" className="campo" value={email}
                     onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor="pa-desconto">Desconto %</label>
              <CampoNumero id="pa-desconto" className="campo" valor={desconto} onValor={setDesconto} />
            </div>
          </div>
          <div>
            <label className="rotulo" htmlFor="pa-senha">Senha (mínimo 8)</label>
            <input id="pa-senha" type="text" className="campo" value={senha}
                   onChange={(e) => setSenha(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={limpar}>
              Voltar
            </button>
            <button type="button" className="botao-primario flex-1 py-2 text-sm"
                    disabled={!clienteId || senha.length < 8 || criar.isPending}
                    onClick={() => criar.mutate()}>
              Criar acesso
            </button>
          </div>
        </div>
      )}

      {contas.isPending && <Carregando />}
      <ul className="space-y-2">
        {(contas.data ?? []).map((c) => (
          <li key={c.user_id} className={`cartao p-3 ${!c.ativo ? 'opacity-60' : ''}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold">
                  {c.customers?.nome ?? 'cliente removido'}
                  {!c.ativo && <span className="ml-1 text-xs text-dim">(desativado)</span>}
                </p>
                <p className="text-xs text-mute">
                  desconto {String(c.desconto_pct).replace('.', ',')}% · criado {dataHoraBR(c.created_at)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="botao-terciario"
                        onClick={() => { setDescontoDe(descontoDe === c.user_id ? null : c.user_id); setDescontoNovo(Number(c.desconto_pct)); }}>
                  Desconto
                </button>
                <button type="button" className="botao-terciario" disabled={!c.ativo}
                        onClick={() => { setSenhaDe(senhaDe === c.user_id ? null : c.user_id); setSenhaNova(''); }}>
                  Senha
                </button>
                <button type="button" className="botao-terciario" disabled={alternar.isPending}
                        onClick={() => (c.ativo
                          ? setDesativando(desativando === c.user_id ? null : c.user_id)
                          : alternar.mutate({ id: c.user_id, desativar: false }))}>
                  {c.ativo ? 'Desativar' : 'Reativar'}
                </button>
                {dono && (
                  <button type="button" className="text-xs text-erro underline"
                          onClick={() => setExcluindo(excluindo === c.user_id ? null : c.user_id)}>
                    excluir
                  </button>
                )}
              </div>
            </div>

            {descontoDe === c.user_id && (
              <div className="mt-2 flex items-end gap-2 border-t border-edge pt-2">
                <div className="flex-1">
                  <label className="rotulo" htmlFor={`pd-${c.user_id}`}>Novo desconto %</label>
                  <CampoNumero id={`pd-${c.user_id}`} className="campo"
                               valor={descontoNovo} onValor={setDescontoNovo} />
                </div>
                <button type="button" className="botao-primario px-3 py-2 text-sm"
                        disabled={descontoNovo == null || descontoNovo < 0 || descontoNovo > 100
                                  || salvarDesconto.isPending}
                        onClick={() => salvarDesconto.mutate()}>
                  Salvar
                </button>
              </div>
            )}
            {senhaDe === c.user_id && (
              <div className="mt-2 flex items-end gap-2 border-t border-edge pt-2">
                <div className="flex-1">
                  <label className="rotulo" htmlFor={`ps-${c.user_id}`}>Senha nova (mínimo 8)</label>
                  <input id={`ps-${c.user_id}`} type="text" className="campo" value={senhaNova}
                         onChange={(e) => setSenhaNova(e.target.value)} />
                </div>
                <button type="button" className="botao-primario px-3 py-2 text-sm"
                        disabled={senhaNova.length < 8 || redefinir.isPending}
                        onClick={() => redefinir.mutate()}>
                  Salvar
                </button>
              </div>
            )}
            {desativando === c.user_id && (
              <div className="mt-2 space-y-2 border border-alerta/40 bg-alerta/5 p-3">
                <p className="text-xs text-mute">
                  O acesso cai na hora: o parceiro é desconectado e não consegue
                  entrar de novo até você reativar. Os pedidos dele ficam.
                </p>
                <div className="flex gap-2">
                  <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                          onClick={() => setDesativando(null)}>Voltar</button>
                  <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                          disabled={alternar.isPending}
                          onClick={() => {
                            setDesativando(null);
                            alternar.mutate({ id: c.user_id, desativar: true });
                          }}>
                    Desativar acesso
                  </button>
                </div>
              </div>
            )}
            {excluindo === c.user_id && (
              <div className="mt-2 space-y-2 border border-erro/40 bg-erro/5 p-3">
                <p className="text-xs text-mute">
                  Apaga o login de vez — só serve para acesso criado por engano.
                  Parceiro com pedido no histórico não pode ser excluído: use
                  "Desativar", que corta o acesso na hora.
                </p>
                <div className="flex gap-2">
                  <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                          onClick={() => setExcluindo(null)}>Voltar</button>
                  <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                          disabled={excluir.isPending}
                          onClick={() => excluir.mutate(c.user_id)}>
                    Excluir de vez
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
        {(contas.data ?? []).length === 0 && !contas.isPending && !contas.error && !criando && (
          <li className="text-sm text-dim">Nenhum acesso criado ainda.</li>
        )}
      </ul>
    </div>
  );
}
